import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminLogos.css";

/* webpack require.context for logo images */
let LOGO_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/logos",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  LOGO_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function logoImg(filename) {
  if (!filename) return null;
  return LOGO_IMAGES[filename] ?? `${API_BASE}/uploads/logos/${filename}`;
}

const PLACEMENTS = ["both", "header", "footer"];

const PLACEMENT_CONFIG = {
  both:   { label: "Header & Footer", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  header: { label: "Header Only",     color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  footer: { label: "Footer Only",     color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
};

const EMPTY_FORM = {
  title:          "",
  alt_text:       "",
  placement:      "both",
  order:          "0",
  active:         1,
  image:          null, // new File upload
};

/* ─────────────────────────────────────
   Placement Badge
   ───────────────────────────────────── */
function PlacementBadge({ placement }) {
  const cfg = PLACEMENT_CONFIG[placement] || { label: placement, color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" };
  return (
    <span
      className="algo-placement-badge"
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
    >
      {cfg.label}
    </span>
  );
}

/* ─────────────────────────────────────
   Status Badge
   ───────────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`algo-badge ${isActive ? "algo-badge--active" : "algo-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ─────────────────────────────────────
   Dot Menu
   ───────────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="algo-dot-menu" ref={ref}>
      <button
        className="algo-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="algo-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="algo-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="algo-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="algo-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="algo-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ logo, onClose }) {
  if (!logo) return null;

  const src = logoImg(logo.image_filename);

  return (
    <div className="algo-modal-overlay" onClick={onClose}>
      <div className="algo-modal algo-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="algo-modal__header">
          <div className="algo-modal__title-row">
            <span className="algo-modal__tag">VIEW</span>
            <h2 className="algo-modal__title">{logo.title || "Logo"}</h2>
          </div>
          <button className="algo-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="algo-modal__body algo-modal__body--view">
          {/* Logo preview */}
          <div className="algo-view-preview">
            {src ? (
              <img src={src} alt={logo.alt_text || logo.title || ""} className="algo-view-image" />
            ) : (
              <div className="algo-view-image--placeholder">
                <span>🖼</span>
                <p>No image uploaded</p>
              </div>
            )}
          </div>

          {/* Details grid */}
          <div className="algo-view-grid">
            {[
              ["Title",     logo.title     || "—"],
              ["Alt Text",  logo.alt_text  || "—"],
              ["Placement", PLACEMENT_CONFIG[logo.placement]?.label || logo.placement || "—"],
              ["Order",     logo.order     ?? "—"],
              ["Status",    Number(logo.active) === 1 ? "Active" : "Inactive"],
              ["Created",   logo.created_at ? new Date(logo.created_at).toLocaleDateString("en-ZA") : "—"],
            ].map(([label, val]) => (
              <div key={label} className="algo-view-row">
                <span className="algo-view-row__label">{label}</span>
                <span className="algo-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Filename */}
          {logo.image_filename && (
            <div className="algo-view-filename">
              <span className="algo-view-filename__label">File</span>
              <code className="algo-view-filename__val">{logo.image_filename}</code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, logo, onClose, onSaved }) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);
  const [clearImg, setClearImg] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (mode === "edit" && logo) {
      setForm({
        title:     logo.title     || "",
        alt_text:  logo.alt_text  || "",
        placement: logo.placement || "both",
        order:     logo.order     ?? "0",
        active:    Number(logo.active) === 1 ? 1 : 0,
        image:     null,
      });
      setPreview(logo.image_filename ? logoImg(logo.image_filename) : null);
      setClearImg(false);
    } else {
      setForm(EMPTY_FORM);
      setPreview(null);
      setClearImg(false);
    }
  }, [mode, logo]);

  const set = (key, val) => { setForm((f) => ({ ...f, [key]: val })); setError(""); };

  const handleFile = (file) => {
    if (!file) return;
    set("image", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    set("image", null);
    setPreview(null);
    setClearImg(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) return setError("Logo title is required.");
    if (mode === "create" && !form.image) return setError("An image file is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",     form.title.trim());
      fd.append("alt_text",  form.alt_text.trim());
      fd.append("placement", form.placement);
      fd.append("order",     parseInt(form.order) || 0);
      fd.append("active",    form.active);
      if (form.image)  fd.append("image_filename", form.image);
      if (clearImg)    fd.append("clear_image", "1");

      if (mode === "create") {
        await apiFetch("/api/admin/logos", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/logos/${logo.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="algo-modal-overlay" onClick={onClose}>
      <div className="algo-modal algo-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="algo-modal__header">
          <div className="algo-modal__title-row">
            <span className="algo-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="algo-modal__title">
              {mode === "create" ? "Add Logo" : "Edit Logo"}
            </h2>
          </div>
          <button className="algo-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="algo-modal__body algo-modal__body--form">
          {error && <div className="algo-modal__error">⚠ {error}</div>}

          {/* ── Logo Details ── */}
          <div className="algo-form-section">
            <h3 className="algo-form-section__title">Logo Details</h3>
            <div className="algo-form-grid">
              <div className="algo-form-field">
                <label>Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Main Logo"
                />
              </div>
              <div className="algo-form-field">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={form.alt_text}
                  onChange={(e) => set("alt_text", e.target.value)}
                  placeholder="e.g. Company Name Logo"
                />
                <p className="algo-form-hint">Shown if image fails to load — important for accessibility.</p>
              </div>
            </div>
          </div>

          {/* ── Image Upload ── */}
          <div className="algo-form-section">
            <h3 className="algo-form-section__title">
              Logo Image <span className="algo-form-section__optional">{mode === "create" ? "(required)" : "(optional — leave blank to keep current)"}</span>
            </h3>
            <div className="algo-upload-area">
              <div
                className={`algo-drop-zone ${preview ? "algo-drop-zone--has-image" : ""}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="algo-drop-zone__preview" />
                ) : (
                  <div className="algo-drop-zone__placeholder">
                    <span className="algo-drop-zone__icon">🖼</span>
                    <span className="algo-drop-zone__text">Click to upload logo</span>
                    <span className="algo-drop-zone__sub">PNG, SVG, WebP, JPG — max 10 MB</span>
                    <span className="algo-drop-zone__sub">Transparent backgrounds recommended</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              <div className="algo-upload-actions">
                <button type="button" className="algo-image-btn" onClick={() => fileRef.current?.click()}>
                  {preview ? "Change Image" : "Upload Image"}
                </button>
                {preview && (
                  <button type="button" className="algo-image-btn algo-image-btn--remove" onClick={handleClear}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Display Options ── */}
          <div className="algo-form-section">
            <h3 className="algo-form-section__title">Display Options</h3>
            <div className="algo-form-grid">
              <div className="algo-form-field">
                <label>Placement</label>
                <select
                  value={form.placement}
                  onChange={(e) => set("placement", e.target.value)}
                >
                  {PLACEMENTS.map((p) => (
                    <option key={p} value={p}>{PLACEMENT_CONFIG[p]?.label || p}</option>
                  ))}
                </select>
                <p className="algo-form-hint">Where this logo will be displayed on the site.</p>
              </div>
              <div className="algo-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                />
                <p className="algo-form-hint">Lower numbers appear first.</p>
              </div>
              <div className="algo-form-field algo-form-field--check-col">
                <label className="algo-form-label--block">Visibility</label>
                <label className="algo-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="algo-modal__footer">
          <button className="algo-btn algo-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="algo-btn algo-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Logo" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ logo, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/logos/${logo.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="algo-modal-overlay" onClick={onClose}>
      <div className="algo-modal algo-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="algo-delete__icon">🗑</div>
        <h2 className="algo-delete__title">Delete Logo?</h2>
        <p className="algo-delete__body">
          You are about to permanently delete{" "}
          <strong>{logo?.title || "this logo"}</strong> and its image file from disk.
          This action cannot be undone.
        </p>
        {error && <div className="algo-modal__error">⚠ {error}</div>}
        <div className="algo-delete__actions">
          <button className="algo-btn algo-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="algo-btn algo-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminLogos() {
  const [logos,      setLogos]      = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");

  /* Filters */
  const [search,          setSearch]          = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [placementFilter, setPlacementFilter] = useState("");
  const [sort,            setSort]            = useState("order_asc");
  const [page,            setPage]            = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load ── */
  const loadLogos = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)          q.set("search",    search);
    if (statusFilter)    q.set("active",    statusFilter);
    if (placementFilter) q.set("placement", placementFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/logos?${q}`)
      .then((data) => {
        setLogos(data.logos       || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, placementFilter, sort, page]);

  useEffect(() => { loadLogos(); }, [loadLogos]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const handleToggle = async (item) => {
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`/api/admin/logos/${item.id}/toggle`, { method: "PUT" });
      setSuccess(res.message || "Status updated.");
      loadLogos();
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal  = (type, item = null) => { setModal(type); setTarget(item); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = (msg) => {
    closeModal();
    setSuccess(msg || "Saved successfully.");
    loadLogos();
  };

  /* Stats */
  const totalActive   = logos.filter((l) => Number(l.active) === 1).length;
  const totalInactive = logos.filter((l) => Number(l.active) !== 1).length;

  const formatDate = (dt) => {
    if (!dt) return "—";
    return new Date(dt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <AdminLayout pageTag="SETTINGS" pageTitle="Logos">

      {/* ── Intro bar ── */}
      <div className="algo-intro-bar">
        <div className="algo-intro-bar__left">
          <span className="algo-intro-bar__icon">🏷</span>
          <div>
            <h2 className="algo-intro-bar__title">Logos</h2>
            <p className="algo-intro-bar__sub">Manage site logos displayed in the header and footer.</p>
          </div>
        </div>
        <div className="algo-intro-bar__right">
          <span className="algo-stat-pill">
            <span className="algo-stat-pill__num">{pagination.total}</span> Total
          </span>
          <span className="algo-stat-pill">
            <span className="algo-stat-pill__num">{totalActive}</span> Active
          </span>
          <span className="algo-stat-pill">
            <span className="algo-stat-pill__num">{totalInactive}</span> Inactive
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="algo-alert algo-alert--error">⚠ {error}</div>}
      {success && <div className="algo-alert algo-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="algo-toolbar">
        <div className="algo-toolbar__left">
          <div className="algo-search-wrap">
            <span className="algo-search-wrap__icon">⌕</span>
            <input
              className="algo-search"
              type="text"
              placeholder="Search by title or alt text…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="algo-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="algo-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="algo-filter-select"
            value={placementFilter}
            onChange={(e) => { setPlacementFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Placements</option>
            {PLACEMENTS.map((p) => (
              <option key={p} value={p}>{PLACEMENT_CONFIG[p]?.label || p}</option>
            ))}
          </select>

          <select
            className="algo-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="algo-toolbar__right">
          <span className="algo-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} logo${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="algo-btn-primary" onClick={() => openModal("create")}>
            + Add Logo
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="algo-table-skeleton" />
      ) : logos.length === 0 ? (
        <div className="algo-empty">
          <span className="algo-empty__icon">🏷</span>
          <h3 className="algo-empty__title">No Logos Found</h3>
          <p className="algo-empty__sub">
            {search || statusFilter || placementFilter
              ? "Try adjusting your filters."
              : "Click \"Add Logo\" to upload the first logo."}
          </p>
          {(search || statusFilter || placementFilter) && (
            <button
              className="algo-btn algo-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPlacementFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="algo-table-wrap">
          <table className="algo-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Title</th>
                <th>Alt Text</th>
                <th>Placement</th>
                <th>Order</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logos.map((item) => (
                <tr
                  key={item.id}
                  className={Number(item.active) !== 1 ? "algo-row--inactive" : ""}
                >
                  <td>
                    <div className="algo-preview-cell">
                      {item.image_filename ? (
                        <img
                          src={logoImg(item.image_filename)}
                          alt={item.alt_text || item.title || ""}
                          className="algo-img-thumb"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="algo-img-placeholder">🏷</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="algo-title-cell">{item.title || "—"}</span>
                    <span className="algo-id-cell">ID #{item.id}</span>
                  </td>
                  <td>
                    <span className="algo-alt-cell">{item.alt_text || <span className="algo-muted">—</span>}</span>
                  </td>
                  <td>
                    <PlacementBadge placement={item.placement} />
                  </td>
                  <td>
                    <span className="algo-order-badge">{item.order ?? 0}</span>
                  </td>
                  <td>
                    <span className="algo-date-cell">{formatDate(item.created_at)}</span>
                  </td>
                  <td>
                    <StatusBadge active={item.active} />
                  </td>
                  <td>
                    <div className="algo-actions">
                      <button
                        className={`algo-icon-btn ${
                          Number(item.active) === 1
                            ? "algo-icon-btn--toggle-on"
                            : "algo-icon-btn--toggle-off"
                        }`}
                        title={Number(item.active) === 1 ? "Deactivate" : "Activate"}
                        onClick={() => handleToggle(item)}
                      >
                        {Number(item.active) === 1 ? "✓" : "○"}
                      </button>
                      <DotMenu
                        onView={()   => openModal("view",   item)}
                        onEdit={()   => openModal("edit",   item)}
                        onDelete={() => openModal("delete", item)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="algo-pagination">
          <button
            className="algo-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`algo-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="algo-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   logo={target}  onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"  onClose={closeModal} onSaved={() => afterSave("Logo created successfully.")} />}
      {modal === "edit"   && <FormModal   mode="edit"    logo={target} onClose={closeModal} onSaved={() => afterSave("Logo updated successfully.")} />}
      {modal === "delete" && <DeleteModal logo={target}  onClose={closeModal} onDeleted={() => afterSave("Logo deleted.")} />}

    </AdminLayout>
  );
}